import React, { useState } from 'react';
import { Link } from 'react-router';
import { Svg } from 'src/components/Svg/Svg';
import { productOptions_productOptions_items_ConfigurableProduct as Options } from 'src/queries/__generated__/productOptions';
import { Price } from 'src/components/Helpers/Price';
import classnames from 'classnames';
import { optionsToSizes } from 'src/components/ProductDetail/utils/product-detail.types';
import { ProductConfigOptions } from 'src/components/ProductDetail/ProductOptions';

import { ProductListItem } from '../CategoryListingPage/utils/productListItem';
import { ColourPickerSwatch } from '../ProductDetail/utils/product-detail.types';
import { ProductListColourPicker } from '../CategoryListingPage/ProductListColourPicker';
import { SwatchKind } from '../ProductDetail/utils/swatchData';
import { CircleLoader } from '../CircleLoader/CircleLoader';

import { UpsellActions } from './UpsellActions';
import classes from './UpsellItem.scss';

type ItemProps = {
    product: ProductListItem;
    gtmSelectItem(index: number): void;
    index: number;
    bestSellers?: Set<number>;
    tagsAggregation?: never[] | { label: string; value: string }[];
};

export const UpsellItem: React.FC<ItemProps> = ({ product, gtmSelectItem, index, bestSellers, tagsAggregation }) => {
    const isTaggedAsBestseller = product?.product_tags
        ?.map((pTag) => tagsAggregation?.find((tag) => tag.value === pTag)?.label)
        ?.includes('Bestseller');
    const displayBestsellerTag = (bestSellers && bestSellers.has(product.id)) || isTaggedAsBestseller;

    const [currentColor, setCurrentColor] = useState<ColourPickerSwatch | null>(null);
    const [defaultColor, setDefaultColor] = useState<ColourPickerSwatch | null>(null);
    const [, setOptions] = useState<Options | null>(null);
    const [isHovered, setIsHovered] = useState(false);
    const [isOpenOverlay, setIsOpenOverlay] = useState(false);
    const [swatches, setSwatches] = useState<ColourPickerSwatch[]>([]);
    const [pending, setPending] = useState(false);

    let displayImage = product.small_image.url;

    if (currentColor) {
        displayImage =
            isHovered && currentColor.swatch_image_url ? currentColor.swatch_image_url : currentColor.product_small_url;
    } else if (defaultColor?.swatch_image_url && isHovered) {
        displayImage = defaultColor.swatch_image_url;
    }

    const sizes = optionsToSizes(product?.configurable_options as ProductConfigOptions[]);
    const isProductOutOfStock = product.stock_status === 'OUT_OF_STOCK' || product.stock_qty === 0;

    const computedSwatches = swatches.map((swatch) => {
        const isImageSwatch = swatch.kind === 'ImageSwatchData' || swatch.kind === '2';
        return {
            title: swatch.label,
            value_index: Number(swatch.color_id),
            swatch_data: {
                kind: isImageSwatch ? SwatchKind.ImageSwatchData : SwatchKind.ColorSwatchData,
                value: swatch.value,
            },
            hex: '',
        };
    });

    return (
        <div className={classes.wrapper}>
            <Link to={`/${product.url_key || ''}`} className={classes.image} onClick={() => gtmSelectItem(index)}>
                {product.is_24_hour_delivery && <Svg className={classes.is24HourDelivery} name="24hr" />}
                <img
                    src={displayImage}
                    alt={product.small_image.label || product.name}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                />
                {displayBestsellerTag && (
                    <small className={classes.bestseller}>
                        <div>
                            <Svg name="star" /> Bestseller
                        </div>
                    </small>
                )}
            </Link>

            <div className={classes.content}>
                <div className={classes.title}>{product.name}</div>
                <div className={classes.swatches}>
                    <ProductListColourPicker
                        onChange={setCurrentColor}
                        onDefaultColorLoad={setDefaultColor}
                        setOptions={setOptions}
                        productId={product.id}
                        noBreakLines
                        setSwatches={setSwatches}
                    />
                </div>
            </div>
            <div className={classes.priceAndAction}>
                <div className={classes.priceContainer}>
                    <div className={classes.priceExcl}>
                        <Price priceIncl={product.price.price} price={product.price.price} /> ex VAT
                    </div>
                    <div className={classes.priceInc}>
                        <Price priceIncl={product.price.price_incl} price={product.price.price_incl} /> inc VAT
                    </div>
                </div>
                <div className={classnames(classes.action, { [classes.actionDisabled]: isProductOutOfStock })}>
                    {pending ? (
                        <div className={classes.loaderWrapper}>
                            <CircleLoader />
                        </div>
                    ) : (
                        <button
                            className={classes.button}
                            type="button"
                            disabled={isProductOutOfStock}
                            title={isProductOutOfStock ? 'Out of stock' : undefined}
                            onClick={() => {
                                if (isProductOutOfStock) return;
                                setIsOpenOverlay(true);
                            }}
                        >
                            <img
                                className={classes.actionIcon}
                                src={require('../../../static/icons/add-to-basket-icon.svg')}
                                alt="Add to basket"
                            />
                            <img
                                className={classes.actionIconOrange}
                                src={require('../../../static/icons/add-to-basket-icon-orange.svg')}
                                alt=""
                            />
                        </button>
                    )}
                </div>
            </div>
            <div
                className={classnames(classes.overlay, {
                    [classes.open]: isOpenOverlay,
                })}
            >
                <div className={classes.overlayDetails}>
                    <div className={classes.overlayName}>{product.name}</div>
                    <div className={classes.overlaySku}>{product.sku}</div>
                </div>
                <div className={classes.overlayActions}>
                    {isOpenOverlay && sizes.length > 0 && (
                        <UpsellActions
                            sizes={sizes}
                            swatches={computedSwatches}
                            parentSku={product.sku}
                            variants={product.variants || []}
                            initialColorId={Number(currentColor?.color_id || defaultColor?.color_id)}
                            onClose={() => setIsOpenOverlay(false)}
                            setPending={setPending}
                        />
                    )}
                </div>
                <div className={classes.overlayCloseIcon}>
                    <button type="button" className={classes.closeIcon} onClick={() => setIsOpenOverlay(false)}>
                        <img
                            className={classes.actionIcon}
                            src={require('../../../static/icons/close-icon.svg')}
                            alt=""
                        />
                    </button>
                </div>
            </div>
        </div>
    );
};
